/**
 * Renders question text with pipe-table support.
 * Lines that start AND end with | become bordered table cells.
 *
 * Cell sizing is adaptive:
 *   single emoji  → large (counting tables: one object per cell)
 *   multiple emoji → medium (equation tables: groups in one cell)
 *   operator (+−=) → bold, neutral bg
 *   blank (___) → muted answer slot
 *   label text → compact
 */

import MathText from "./MathText";

const EMOJI_RE = /\p{Extended_Pictographic}️?/gu;

function emojiCount(s) {
  return (s.match(EMOJI_RE) || []).length;
}

function parseLine(line) {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-extrabold"><MathText>{part.slice(2, -2)}</MathText></strong>
      : <MathText key={i}>{part}</MathText>
  );
}

function isTableRow(line) {
  const t = line.trim();
  return t.startsWith("|") && t.endsWith("|") && t.length > 2;
}

function TableCell({ content, compact }) {
  const isOp    = /^[+\-−×÷=]$/.test(content);
  const isBlank = /^_+$/.test(content.replace(/\s/g, ""));
  const ec      = emojiCount(content);
  const textOnly = content.replace(EMOJI_RE, "").trim();
  const pureEmoji = ec > 0 && textOnly === "";
  const singleEmoji = pureEmoji && ec === 1;
  const multiEmoji  = pureEmoji && ec > 1;

  let cls = "border-[2.5px] border-[#0A0A0A] text-center font-mono font-bold leading-none ";
  if (isOp) {
    cls += "text-2xl px-2 py-2 bg-[#F9F9F6] min-w-[2.5rem]";
  } else if (isBlank) {
    cls += "text-muted-foreground px-4 py-3 bg-white min-w-[5rem]";
  } else if (singleEmoji) {
    // compact mode (≥8 cols): smaller cells so 10-column grids fit without scrolling
    cls += compact
      ? "text-2xl p-1 min-w-[2rem]"
      : "text-5xl px-4 py-4 min-w-[4.5rem]";
  } else if (multiEmoji) {
    cls += "text-2xl px-3 py-3 min-w-[3rem]";
  } else {
    cls += "text-sm px-3 py-2 min-w-[3rem]";
  }

  return <td className={cls}>{parseLine(content)}</td>;
}

function TableRowEl({ row, compact }) {
  const cells = row.trim().split("|").slice(1, -1);
  return (
    <tr>
      {cells.map((cell, ci) => (
        <TableCell key={ci} content={cell.trim()} compact={compact} />
      ))}
    </tr>
  );
}

function TenFrame({ n }) {
  return (
    <div style={{ display: "inline-grid", border: "2.5px solid #0A0A0A", gridTemplateColumns: "repeat(5, 1fr)" }}>
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} style={{
          width: "40px", height: "40px",
          border: "1px solid #b0cfe0",
          backgroundColor: i < n ? "#cce4f5" : "white",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {i < n && <div style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#5b9bd5", border: "2px solid #3a78b5" }} />}
        </div>
      ))}
    </div>
  );
}

function DoubleTenFrame({ n }) {
  return (
    <div className="flex gap-3">
      {[0, 10].map((offset, fi) => {
        const filled = Math.max(0, Math.min(10, n - offset));
        return (
          <div key={fi} className="inline-grid border-[2.5px] border-[#0A0A0A]"
               style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i}
                   className={`w-10 h-10 border border-[#b0cfe0] flex items-center justify-center
                     ${i < filled ? "bg-[#cce4f5]" : "bg-white"}`}>
                {i < filled && (
                  <div className="w-7 h-7 rounded-full bg-[#5b9bd5] border-2 border-[#3a78b5]" />
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function Base10Blocks({ n }) {
  const rods = Math.floor(n / 10);
  const ones = n % 10;
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
      {Array.from({ length: rods }, (_, ri) => (
        <div key={ri} style={{
          display: "flex", flexDirection: "column", gap: "1px",
          padding: "1px", border: "2px solid #1a6a5e",
        }}>
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} style={{
              width: "22px", height: "12px",
              backgroundColor: "#4db5a5", border: "1px solid #2a8a7e",
            }} />
          ))}
        </div>
      ))}
      {ones > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
          {Array.from({ length: ones }, (_, i) => (
            <div key={i} style={{
              width: "22px", height: "12px",
              backgroundColor: "#7dcfc5", border: "2px solid #2a8a7e",
            }} />
          ))}
        </div>
      )}
    </div>
  );
}

function NumberChart20({ missing }) {
  const missingSet = new Set(missing);
  return (
    <div className="inline-grid border-[2px] border-[#0A0A0A]"
         style={{ gridTemplateColumns: "repeat(10, 1fr)" }}>
      {Array.from({ length: 20 }, (_, i) => {
        const num = i + 1;
        const isMissing = missingSet.has(num);
        return (
          <div key={num}
               className={`w-10 h-9 border border-gray-300 flex items-center justify-center font-mono font-bold text-sm
                 ${isMissing ? "bg-[#b3d9ee] text-[#1e5fa8]" : "bg-white text-[#0A0A0A]"}`}>
            {isMissing ? "?" : num}
          </div>
        );
      })}
    </div>
  );
}

function TripleTenFrame({ n }) {
  const filledCounts = [10, 10, n - 20];
  return (
    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
      {filledCounts.map((filled, fi) => (
        <div key={fi} style={{
          display: "inline-grid",
          border: "2.5px solid #0A0A0A",
          gridTemplateColumns: "repeat(5, 1fr)",
        }}>
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} style={{
              width: "34px", height: "34px",
              border: "1px solid #b0cfe0",
              backgroundColor: i < filled ? "#cce4f5" : "white",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {i < filled && (
                <div style={{
                  width: "22px", height: "22px",
                  borderRadius: "50%",
                  backgroundColor: "#5b9bd5",
                  border: "2px solid #3a78b5",
                }} />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function QuadTenFrame({ n }) {
  const filledCounts = [0, 1, 2, 3].map((f) => Math.max(0, Math.min(10, n - f * 10)));
  return (
    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
      {filledCounts.map((filled, fi) => (
        <div key={fi} style={{
          display: "inline-grid",
          border: "2.5px solid #0A0A0A",
          gridTemplateColumns: "repeat(5, 1fr)",
        }}>
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} style={{
              width: "28px", height: "28px",
              border: "1px solid #b0cfe0",
              backgroundColor: i < filled ? "#cce4f5" : "white",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {i < filled && (
                <div style={{
                  width: "19px", height: "19px",
                  borderRadius: "50%",
                  backgroundColor: "#5b9bd5",
                  border: "2px solid #3a78b5",
                }} />
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function NumberChart30({ n }) {
  return (
    <div style={{
      display: "inline-grid",
      gridTemplateColumns: "repeat(10, 1fr)",
      border: "2px solid #0A0A0A",
    }}>
      {Array.from({ length: 30 }, (_, i) => {
        const num = i + 1;
        const isMissing = num === n;
        return (
          <div key={num} style={{
            width: "34px", height: "28px",
            border: "1px solid #ccc",
            backgroundColor: isMissing ? "#b3d9ee" : "white",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "monospace", fontWeight: "bold", fontSize: "12px",
            color: isMissing ? "#1e5fa8" : "#0A0A0A",
          }}>
            {isMissing ? "?" : num}
          </div>
        );
      })}
    </div>
  );
}

function NumberChart100({ n }) {
  return (
    <div style={{
      display: "inline-grid",
      gridTemplateColumns: "repeat(10, 1fr)",
      border: "2px solid #0A0A0A",
    }}>
      {Array.from({ length: 100 }, (_, i) => {
        const num = i + 1;
        const isMissing = num === n;
        return (
          <div key={num} style={{
            width: "30px", height: "24px",
            border: "1px solid #ccc",
            backgroundColor: isMissing ? "#b3d9ee" : (num % 10 === 0 ? "#f5f5e8" : "white"),
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "monospace", fontWeight: isMissing ? "bold" : "normal",
            fontSize: "11px",
            color: isMissing ? "#1e5fa8" : "#0A0A0A",
          }}>
            {isMissing ? "?" : num}
          </div>
        );
      })}
    </div>
  );
}

function _hollowRectDims(n) {
  if (n <= 4) return [1, n];
  let bestR = -1, bestC = -1, bestPerim = Infinity;
  for (let r = 3; r <= n; r++) {
    const c = Math.max(r, Math.ceil((n + 4 - 2 * r) / 2));
    const perim = 2 * (r + c) - 4;
    if (perim < bestPerim || (perim === bestPerim && c - r < bestC - bestR)) {
      bestR = r; bestC = c; bestPerim = perim;
    }
    if (c === r) break; // square is the most compact shape — no better solution exists beyond this
  }
  return [bestR, bestC];
}

function RectArray({ n, emoji }) {
  const [rows, cols] = _hollowRectDims(n);
  const emSize = cols <= 4 ? 28 : cols <= 6 ? 22 : 18;
  const gap = 5;
  const cell = emSize + gap;

  const posSet = new Set();
  let placed = 0;
  for (let c = 0; c < cols && placed < n; c++) { posSet.add(`0,${c}`); placed++; }
  for (let r = 1; r < rows - 1 && placed < n; r++) { posSet.add(`${r},${cols - 1}`); placed++; }
  if (rows > 1) for (let c = cols - 1; c >= 0 && placed < n; c--) { posSet.add(`${rows - 1},${c}`); placed++; }
  if (cols > 1) for (let r = rows - 2; r >= 1 && placed < n; r--) { posSet.add(`${r},0`); placed++; }

  return (
    <div style={{ display: "inline-grid", gridTemplateColumns: `repeat(${cols}, ${cell}px)`, gap: "0" }}>
      {Array.from({ length: rows * cols }, (_, i) => {
        const r = Math.floor(i / cols), c = i % cols;
        return (
          <div key={i} style={{ width: `${cell}px`, height: `${cell}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {posSet.has(`${r},${c}`) ? <span style={{ fontSize: `${emSize}px`, lineHeight: 1 }}>{emoji}</span> : null}
          </div>
        );
      })}
    </div>
  );
}

function TriArray({ n, emoji }) {
  const k = Math.max(2, Math.ceil((n + 3) / 3));
  const emSize = k <= 4 ? 28 : k <= 6 ? 22 : 18;
  const gap = 5;
  const cell = emSize + gap;

  // Positions: left column (top→bottom), bottom row (left→right), hypotenuse (bottom→top)
  const posSet = new Set();
  let placed = 0;
  for (let r = 0; r < k && placed < n; r++) { posSet.add(`${r},0`); placed++; }
  for (let c = 1; c < k && placed < n; c++) { posSet.add(`${k - 1},${c}`); placed++; }
  for (let d = k - 2; d >= 1 && placed < n; d--) { posSet.add(`${d},${d}`); placed++; }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
      {Array.from({ length: k }, (_, r) => (
        <div key={r} style={{ display: "flex" }}>
          {Array.from({ length: r + 1 }, (_, c) => (
            <div key={c} style={{ width: `${cell}px`, height: `${cell}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {posSet.has(`${r},${c}`) ? <span style={{ fontSize: `${emSize}px`, lineHeight: 1 }}>{emoji}</span> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function HeartArray({ n, emoji }) {
  const scale = n <= 8 ? 65 : n <= 14 ? 85 : 105;
  const emSize = n <= 8 ? 28 : n <= 14 ? 22 : 18;
  const offset = emSize / 2;
  const w = Math.round(scale * 2 + emSize + 10);
  const h = Math.round(scale * 1.75 + emSize + 10);
  const cx = w / 2;
  const cy = Math.round(scale * 0.7 + offset + 5);
  return (
    <div style={{ position: "relative", width: `${w}px`, height: `${h}px`, margin: "4px 0" }}>
      {Array.from({ length: n }, (_, i) => {
        const t = 2 * Math.PI * i / n;
        const hx = Math.pow(Math.sin(t), 3);
        const hy = (13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t)) / 16;
        const x = cx + hx * scale - offset;
        const y = cy - hy * scale - offset;
        return (
          <div key={i} style={{ position: "absolute", left: `${x}px`, top: `${y}px`, fontSize: `${emSize}px`, lineHeight: 1 }}>
            {emoji}
          </div>
        );
      })}
    </div>
  );
}

function DiamondArray({ n, emoji }) {
  const d = Math.max(1, Math.ceil(n / 4));
  const emSize = d <= 2 ? 24 : d <= 4 ? 18 : 14;
  const gap = d <= 2 ? 4 : 3;
  const cell = emSize + gap;
  const size = 2 * d + 1;

  const positions = [[0, d]];
  for (let r = 1; r <= d; r++) positions.push([r, d + r]);
  for (let r = d + 1; r <= 2 * d; r++) positions.push([r, 3 * d - r]);
  for (let r = 2 * d - 1; r >= d; r--) positions.push([r, r - d]);
  for (let r = d - 1; r >= 1; r--) positions.push([r, d - r]);
  const posSet = new Set(positions.slice(0, n).map(([r, c]) => `${r},${c}`));

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {Array.from({ length: size }, (_, r) => (
        <div key={r} style={{ display: "flex" }}>
          {Array.from({ length: size }, (_, c) => (
            <div key={c} style={{ width: `${cell}px`, height: `${cell}px`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {posSet.has(`${r},${c}`) ? <span style={{ fontSize: `${emSize}px`, lineHeight: 1 }}>{emoji}</span> : null}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

const _SCENE_BG = {
  sky:    "linear-gradient(to bottom, #87CEEB 60%, #90EE90 100%)",
  ocean:  "linear-gradient(to bottom, #1565C0 0%, #1E88E5 50%, #42A5F5 100%)",
  farm:   "linear-gradient(to bottom, #87CEEB 35%, #8FBC8F 65%, #A5D6A7 100%)",
  garden: "linear-gradient(to bottom, #87CEEB 25%, #2E7D32 100%)",
};
const _SCENE_BANK = {
  sky:    ["🦅","🦉","🦜","🦢","🦩","🐦","🐧","🦚"],
  ocean:  ["🐟","🐠","🦀","🐬","🐳","🐙","🦑","🦐"],
  farm:   ["🐓","🐑","🐴","🐐","🦆","🐄","🐷","🐮"],
  garden: ["🐛","🐞","🐝","🦋","🐜","🪲","🦗","🦟"],
};
// 10 scatter positions as [left%, top%] — 4-col × 3-row grid, min ~25% spacing to prevent overlap
const _SCATTER_POS = [
  [5, 5],  [30, 3],  [57, 6],  [82, 2],   // row 1
  [5, 38], [30, 36], [57, 40], [82, 37],  // row 2
  [5, 70], [30, 68],                       // row 3
];

function StickerScene({ scene, n, emoji, name }) {
  const bg = _SCENE_BG[scene] || _SCENE_BG.sky;
  const bankEmojis = _SCENE_BANK[scene] || [];
  const bankCount = Math.min(n + 4, 10);
  return (
    <div style={{ display: "inline-block", margin: "4px 0", width: "380px" }}>
      <div style={{ position: "relative", width: "380px", height: "180px", borderRadius: "8px 8px 0 0", overflow: "hidden", background: bg, border: "2px solid #bbb", borderBottom: "none" }}>
        {_SCATTER_POS.slice(0, n).map(([left, top], i) => (
          <span key={i} style={{ position: "absolute", left: `${left}%`, top: `${top}%`, fontSize: "24px", lineHeight: 1, transform: `rotate(${(i * 43 % 30) - 15}deg)`, userSelect: "none" }}>{emoji}</span>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", padding: "8px 6px", border: "2px solid #bbb", borderTop: "1px solid #ddd", borderRadius: "0 0 8px 8px", background: "#f7f7f7" }}>
        {Array.from({ length: bankCount }, (_, i) => (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "28px", height: "28px", fontSize: "15px", background: "white", borderRadius: "50%", border: "1.5px solid #e0e0e0", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", flexShrink: 0 }}>{bankEmojis[i % bankEmojis.length]}</span>
        ))}
      </div>
    </div>
  );
}

function CircleArray({ n, emoji }) {
  const radius = n <= 6 ? 55 : n <= 10 ? 70 : n <= 15 ? 85 : 100;
  const emSize = n <= 8 ? 30 : n <= 14 ? 24 : 18;
  const offset = emSize / 2;
  const dim = (radius + offset + 6) * 2;
  const center = dim / 2;
  return (
    <div style={{ position: "relative", width: `${dim}px`, height: `${dim}px`, margin: "4px 0" }}>
      {Array.from({ length: n }, (_, i) => {
        const angle = (2 * Math.PI * i / n) - Math.PI / 2;
        const x = center + radius * Math.cos(angle) - offset;
        const y = center + radius * Math.sin(angle) - offset;
        return (
          <div key={i} style={{ position: "absolute", left: `${x}px`, top: `${y}px`, fontSize: `${emSize}px`, lineHeight: 1 }}>
            {emoji}
          </div>
        );
      })}
    </div>
  );
}

function NumberChart100Multi({ missing }) {
  const missingSet = new Set(missing);
  return (
    <div style={{
      display: "inline-grid",
      gridTemplateColumns: "repeat(10, 1fr)",
      border: "2px solid #0A0A0A",
    }}>
      {Array.from({ length: 100 }, (_, i) => {
        const num = i + 1;
        const isMissing = missingSet.has(num);
        return (
          <div key={num} style={{
            width: "30px", height: "24px",
            border: "1px solid #ccc",
            backgroundColor: isMissing ? "#b3d9ee" : (num % 10 === 0 ? "#f5f5e8" : "white"),
            display: "flex", alignItems: "center", justifyContent: "center",
            fontFamily: "monospace", fontWeight: isMissing ? "bold" : "normal",
            fontSize: "11px",
            color: isMissing ? "#1e5fa8" : "#0A0A0A",
          }}>
            {isMissing ? "?" : num}
          </div>
        );
      })}
    </div>
  );
}

export function QuestionText({ text, size = "lg" }) {
  const sizeClass = size === "sm"
    ? "text-sm"
    : size === "base"
    ? "text-base"
    : "text-lg sm:text-xl";

  const lines = (text || "").split("\n");
  const groups = [];
  let currentTable = [];

  for (const line of lines) {
    if (isTableRow(line)) {
      currentTable.push(line);
    } else {
      if (currentTable.length > 0) {
        groups.push({ type: "table", rows: [...currentTable] });
        currentTable = [];
      }
      const trimmed = line.trim();
      if (trimmed === "") continue;
      const tfM = trimmed.match(/^\[TEN_FRAME:(\d+)\]$/);
      if (tfM) { groups.push({ type: "ten_frame", n: parseInt(tfM[1]) }); continue; }
      const dtfM = trimmed.match(/^\[DOUBLE_TEN_FRAME:(\d+)\]$/);
      if (dtfM) { groups.push({ type: "double_ten_frame", n: parseInt(dtfM[1]) }); continue; }
      const b10M = trimmed.match(/^\[BASE10:(\d+)\]$/);
      if (b10M) { groups.push({ type: "base10", n: parseInt(b10M[1]) }); continue; }
      const nc20M = trimmed.match(/^\[NUMBER_CHART_20:([\d,]+)\]$/);
      if (nc20M) { groups.push({ type: "number_chart_20", missing: nc20M[1].split(",").map(Number) }); continue; }
      const ttfM = trimmed.match(/^\[TRIPLE_TEN_FRAME:(\d+)\]$/);
      if (ttfM) { groups.push({ type: "triple_ten_frame", n: parseInt(ttfM[1]) }); continue; }
      const qtfM = trimmed.match(/^\[QUAD_TEN_FRAME:(\d+)\]$/);
      if (qtfM) { groups.push({ type: "quad_ten_frame", n: parseInt(qtfM[1]) }); continue; }
      const nc30M = trimmed.match(/^\[NUMBER_CHART_30:(\d+)\]$/);
      if (nc30M) { groups.push({ type: "number_chart_30", n: parseInt(nc30M[1]) }); continue; }
      const nc100M = trimmed.match(/^\[NUMBER_CHART_100:(\d+)\]$/);
      if (nc100M) { groups.push({ type: "number_chart_100", n: parseInt(nc100M[1]) }); continue; }
      const nc100mM = trimmed.match(/^\[NUMBER_CHART_100_MULTI:([\d,]+)\]$/);
      if (nc100mM) { groups.push({ type: "number_chart_100_multi", missing: nc100mM[1].split(",").map(Number) }); continue; }
      const rectM = trimmed.match(/^\[RECT_ARRAY:(\d+):(?:\d+:)?(.+)\]$/);
      if (rectM) { groups.push({ type: "rect_array", n: parseInt(rectM[1]), emoji: rectM[2] }); continue; }
      const triM = trimmed.match(/^\[TRI_ARRAY:(\d+):(.+)\]$/);
      if (triM) { groups.push({ type: "tri_array", n: parseInt(triM[1]), emoji: triM[2] }); continue; }
      const circM = trimmed.match(/^\[CIRCLE_ARRAY:(\d+):(.+)\]$/);
      if (circM) { groups.push({ type: "circle_array", n: parseInt(circM[1]), emoji: circM[2] }); continue; }
      const heartM = trimmed.match(/^\[HEART_ARRAY:(\d+):(.+)\]$/);
      if (heartM) { groups.push({ type: "heart_array", n: parseInt(heartM[1]), emoji: heartM[2] }); continue; }
      const diaM = trimmed.match(/^\[DIAMOND_ARRAY:(\d+):(.+)\]$/);
      if (diaM) { groups.push({ type: "diamond_array", n: parseInt(diaM[1]), emoji: diaM[2] }); continue; }
      const stickerM = trimmed.match(/^\[STICKER_SCENE:(\w+):(\d+):([^:]+):([^\]]+)\]$/);
      if (stickerM) { groups.push({ type: "sticker_scene", scene: stickerM[1], n: parseInt(stickerM[2]), emoji: stickerM[3], name: stickerM[4] }); continue; }
      groups.push({ type: "text", content: trimmed });
    }
  }
  if (currentTable.length > 0) groups.push({ type: "table", rows: currentTable });

  if (groups.length === 1 && groups[0].type === "text") {
    return (
      <p className={`font-mono ${sizeClass} font-semibold leading-snug`}>
        {parseLine(groups[0].content)}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {groups.map((g, gi) => {
        if (g.type === "text") {
          return (
            <p key={gi} className={`font-mono ${sizeClass} font-semibold leading-snug`}>
              {parseLine(g.content)}
            </p>
          );
        }
        if (g.type === "ten_frame") return <TenFrame key={gi} n={g.n} />;
        if (g.type === "double_ten_frame") return <DoubleTenFrame key={gi} n={g.n} />;
        if (g.type === "base10") return <Base10Blocks key={gi} n={g.n} />;
        if (g.type === "number_chart_20") return <NumberChart20 key={gi} missing={g.missing} />;
        if (g.type === "triple_ten_frame") return <TripleTenFrame key={gi} n={g.n} />;
        if (g.type === "quad_ten_frame") return <QuadTenFrame key={gi} n={g.n} />;
        if (g.type === "number_chart_30") return <NumberChart30 key={gi} n={g.n} />;
        if (g.type === "number_chart_100") return <NumberChart100 key={gi} n={g.n} />;
        if (g.type === "number_chart_100_multi") return <NumberChart100Multi key={gi} missing={g.missing} />;
        if (g.type === "rect_array") return <RectArray key={gi} n={g.n} emoji={g.emoji} />;
        if (g.type === "tri_array") return <TriArray key={gi} n={g.n} emoji={g.emoji} />;
        if (g.type === "circle_array") return <CircleArray key={gi} n={g.n} emoji={g.emoji} />;
        if (g.type === "heart_array") return <HeartArray key={gi} n={g.n} emoji={g.emoji} />;
        if (g.type === "diamond_array") return <DiamondArray key={gi} n={g.n} emoji={g.emoji} />;
        if (g.type === "sticker_scene") return <StickerScene key={gi} scene={g.scene} n={g.n} emoji={g.emoji} name={g.name} />;
        const maxCols = Math.max(...g.rows.map(r => r.trim().split("|").slice(1, -1).length));
        const compact = maxCols >= 8;
        return (
          <div key={gi} className="overflow-x-auto">
            <table className="border-collapse">
              <tbody>
                {g.rows.map((row, ri) => <TableRowEl key={ri} row={row} compact={compact} />)}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export default QuestionText;
